// src/features/ask/currentView.ts
//
// What the person is looking at, for the «Preguntar» panel: the open screen,
// its period and its hotel selection, read from the same stores the screens
// read (shared period, scope preference, per-screen v2 property). The same
// stores are what an assistant action writes — so applying «dame los datos de
// octubre» is exactly what the person would do by hand, and undoing it puts
// the previous values back.
import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslate } from 'react-admin';
import { resourcesConfig } from '@/config/resourceConfig';
import { V2_RESOURCES } from '@/layouts/menuStructure';
import { usePreferences } from '@/providers/preferencesProvider';
import { useAnalyticsPeriod } from '@/providers/analyticsPeriodProvider';
import { useV2Properties } from '@/hooks/useV2PropertySelection';
import type { PeriodValue } from '@/components/common/PeriodPicker';
import type { AskView, AskViewAction } from '@/types/assistant';

/** v1 Dirección screens filtered by the hotel scope selector. */
const HOTEL_SCOPE_SCREENS = new Set(['summary', 'channels', 'dispensers', 'durations', 'payments', 'hotels-overview']);

/** Screens that read the shared period (useAnalyticsPeriod). */
const SHARED_PERIOD_SCREENS = new Set([
  'summary', 'channels', 'dispensers', 'durations', 'payments', 'hotels-overview',
  'operacion', 'satisfaction', 'reconciliation', 'control', 'benchmark',
]);

/** Everything an action may change, to put back on undo. */
export interface ViewSnapshot {
  screen: string;
  period: PeriodValue;
  hotelIds: string[] | null;
  propertyId: string | null;
}

const resolveScreen = (pathname: string) =>
  Object.values(resourcesConfig).find((r) =>
    r.route === '/' ? pathname === '/' : pathname === r.route || pathname.startsWith(`${r.route}/`)
  );

export const useCurrentView = () => {
  const location = useLocation();
  const translate = useTranslate();
  const [period, setPeriod] = useAnalyticsPeriod();
  const { preferences, setPreferences } = usePreferences();
  const { data: v2Properties } = useV2Properties();

  const resource = resolveScreen(location.pathname);
  const screen = resource?.name ?? '';
  const family: AskView['family'] = V2_RESOURCES.has(screen) ? 'pms' : HOTEL_SCOPE_SCREENS.has(screen) ? 'roommatik' : 'none';
  const hasPeriod = SHARED_PERIOD_SCREENS.has(screen);

  // The property a v2 screen actually shows: its remembered choice while still
  // offered, otherwise the first by name — the rule of useV2PropertySelection.
  const remembered = preferences.pageViews[screen]?.propertyId ?? '';
  const sorted = useMemo(
    () => [...(v2Properties ?? [])].sort((a, b) => a.displayName.localeCompare(b.displayName, 'es', { sensitivity: 'base' })),
    [v2Properties]
  );
  const propertyId = family !== 'pms' || sorted.length === 0
    ? null
    : sorted.some((p) => p.id === remembered) ? remembered : sorted[0].id;

  const screenLabel = resource ? translate(resource.label, { smart_count: 1 }) : screen;
  const hotelIds = preferences.scope.hotelIds;
  const view = useMemo<AskView | null>(() => (screen
    ? {
        screen,
        screenLabel,
        family,
        hasPeriod,
        from: hasPeriod ? period.from : undefined,
        to: hasPeriod ? period.to : undefined,
        hotelIds: family === 'roommatik' ? hotelIds : undefined,
        propertyId: family === 'pms' ? propertyId : undefined,
      }
    : null), [screen, screenLabel, family, hasPeriod, period.from, period.to, hotelIds, propertyId]);

  const writeProperty = useCallback(
    (target: string, id: string | null) => {
      const current = preferences.pageViews[target] ?? {};
      const next = { ...current };
      if (id) next.propertyId = id;
      else delete next.propertyId;
      setPreferences({ pageViews: { ...preferences.pageViews, [target]: next } });
    },
    [preferences.pageViews, setPreferences]
  );

  /** Applies an action to the open screen and returns what to restore on undo. */
  const apply = useCallback(
    (action: AskViewAction): ViewSnapshot => {
      const snapshot: ViewSnapshot = {
        screen,
        period,
        hotelIds: preferences.scope.hotelIds,
        propertyId: remembered || null,
      };
      if (action.from && action.to) setPeriod({ ...period, from: action.from, to: action.to });
      if (action.allHotels) setPreferences({ scope: { ...preferences.scope, hotelIds: null } });
      else if (action.hotelIds && action.hotelIds.length > 0) setPreferences({ scope: { ...preferences.scope, hotelIds: action.hotelIds } });
      if (action.propertyId) writeProperty(screen, action.propertyId);
      return snapshot;
    },
    [screen, period, preferences.scope, remembered, setPeriod, setPreferences, writeProperty]
  );

  const restore = useCallback(
    (snapshot: ViewSnapshot) => {
      setPeriod(snapshot.period);
      setPreferences({ scope: { ...preferences.scope, hotelIds: snapshot.hotelIds } });
      if (V2_RESOURCES.has(snapshot.screen)) writeProperty(snapshot.screen, snapshot.propertyId);
    },
    [preferences.scope, setPeriod, setPreferences, writeProperty]
  );

  return { view, apply, restore };
};
